import { Injectable, inject } from '@angular/core';
import { firstValueFrom, race, timer, filter, take, map } from 'rxjs';
import { AITool } from '../tool.decorator';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { RxSalesDataService } from '@omni/services/contact-spotlight/rxsalesdata.service';
import { BrandOfflineService } from '@omni/services/brand/brand.service';
import { AdoptionLadderJourneyService } from '@omni/services/adoption-ladder-journey/adoption-ladder-journey.service';
import { MarketScanService } from '@omni/services/market-scan/market-scan.service';
import { CustomerSegmentationOfflineService } from '@omni/services/customer-segmentation/customer-segmentation-offline.service';
import { ProductRatingOfflineService } from '@omni/services/product-rating/product-rating-offline.service';

/** One-decimal round for change/percent values. */
const round1 = (n: number): number => Math.round((n || 0) * 10) / 10;

/** Normalise a metric to a chart-ready shape: integer value, one-decimal change. */
const roundMetric = (m: { value?: number; change?: number } | undefined) =>
  m ? { value: Math.round(m.value || 0), change: round1(m.change ?? 0) } : m;

/**
 * Rx / sales-data tools — prescription, sales, and adoption-ladder insights
 * built off the contact-spotlight aggregations and MarketScan segmentations.
 *
 * Period codes use the wire short-names (CW, C3W, ..., YTD) and map to the
 * service's numeric `Period` enum positions inline. We don't import the enum
 * here to keep the keying explicit at the tool boundary.
 */
@Injectable({ providedIn: 'root' })
export class RxTools {
  private readonly contactService = inject(ContactOfflineService);
  private readonly rxSalesDataService = inject(RxSalesDataService);
  private readonly brandService = inject(BrandOfflineService);
  private readonly adoptionService = inject(AdoptionLadderJourneyService);
  private readonly marketScanService = inject(MarketScanService);
  private readonly customerSegmentationService = inject(CustomerSegmentationOfflineService);
  private readonly productRatingService = inject(ProductRatingOfflineService);

  /** Flat raw-row set combining segmentation and adoption-ladder rows — each row carries only
   * its own field set (segmentation OR ladder, never both), so concatenating the two independent
   * caches reproduces the shape the old combined fetch used to return. */
  private getSegmentationAndLadderRows(): any[] {
    return [...this.customerSegmentationService.getRawRows(), ...this.productRatingService.getRawRows()];
  }

  /**
   * Adoption ladder rows (customer segmentation rows carry no adoptionLadderId, so the tools
   * below that report ladder position need this wait, not the segmentation one) sync fire-and-
   * forget at the end of the main sync and can take 2+ minutes — see
   * MarketScanService.startLadderBackgroundSync. Races the sync actually finishing against a
   * timeout ceiling, so a tool call never blocks the full timeoutMs unless the sync is genuinely
   * still running that long:
   *  - Already synced (not 'syncing') -> resolves immediately, no wait at all.
   *  - Finishes mid-wait, e.g. at 4s of a 15s window -> resolves at 4s, not 15s.
   *  - Still syncing past timeoutMs -> resolves false; caller should return a "still syncing"
   *    message rather than silently answering with an incomplete/stale array.
   */
  private async waitForAdoptionLadderReady(timeoutMs = 15000): Promise<boolean> {
    const state$ = this.marketScanService.ladderSyncState$;
    if (state$.value !== 'syncing') {
      return true;
    }
    const result = await firstValueFrom(
      race(
        state$.pipe(filter(state => state !== 'syncing'), take(1), map(() => 'ready' as const)),
        timer(timeoutMs).pipe(map(() => 'timeout' as const)),
      ),
    );
    return result !== 'timeout';
  }

  @AITool(
    'get_product_performance',
    'Get prescription/sales performance data for specific products including TRx, NRx, Refills, and sales metrics across different time periods',
    [
      {
        name: 'product_name',
        type: 'string',
        description: 'Product/brand name to get performance for (optional - if not provided, returns all products)',
        required: false,
      },
      {
        name: 'period',
        type: 'string',
        description:
          'Time period: CW (current week), C3W (3 weeks), C6W (6 weeks), CM (current month), C3M (3 months), C6M (6 months), QTD (quarter to date), YTD (year to date)',
        required: false,
        enum: ['CW', 'C3W', 'C6W', 'CM', 'C3M', 'C6M', 'QTD', 'YTD'],
        default: 'CM',
      },
      {
        name: 'contact_id',
        type: 'string',
        description: 'Specific contact ID to get product performance for (optional - filters to specific prescriber)',
        required: false,
      },
    ],
  )
  async getProductPerformance(
    product_name?: string,
    period: 'CW' | 'C3W' | 'C6W' | 'CM' | 'C3M' | 'C6M' | 'QTD' | 'YTD' = 'CM',
    contact_id?: string,
  ): Promise<any> {
    try {
      const rxDataPromise = firstValueFrom(this.rxSalesDataService.rxData$);
      const directSalesDataPromise = firstValueFrom(this.rxSalesDataService.directSalesData$);

      const [rxData, directSalesData] = await Promise.all([rxDataPromise, directSalesDataPromise]);

      const periodEnum = ({
        CW: 1,
        C3W: 2,
        C6W: 3,
        CM: 4,
        C3M: 5,
        C6M: 6,
        QTD: 7,
        YTD: 8,
      } as Record<string, number>)[period] || 4;

      let filteredRxData = rxData.filter((d: any) => d.period === periodEnum);
      let filteredDirectSales = directSalesData.filter((d: any) => d.period === periodEnum);

      if (product_name) {
        const searchLower = product_name.toLowerCase();
        filteredRxData = filteredRxData.filter((d: any) =>
          d.product.name?.toLowerCase().includes(searchLower),
        );
        filteredDirectSales = filteredDirectSales.filter((d: any) =>
          d.product.name?.toLowerCase().includes(searchLower),
        );
      }

      if (contact_id) {
        filteredRxData = filteredRxData.filter((d: any) => d.contact.ID === contact_id);
      }

      const productMap = new Map<string, any>();

      filteredRxData.forEach((item: any) => {
        const productId = item.product.ID;
        if (!productMap.has(productId)) {
          productMap.set(productId, {
            productId,
            productName: item.product.name,
            TRx: { value: 0, change: 0 },
            NRx: { value: 0, change: 0 },
            Refills: { value: 0, change: 0 },
            prescribers: new Set<string>(),
            type: 'prescription',
          });
        }

        const product = productMap.get(productId);
        if (item.TRx) {
          product.TRx.value += item.TRx.value || 0;
          product.TRx.change += item.TRx.change || 0;
        }
        if (item.NRx) {
          product.NRx.value += item.NRx.value || 0;
          product.NRx.change += item.NRx.change || 0;
        }
        if (item.Refills) {
          product.Refills.value += item.Refills.value || 0;
          product.Refills.change += item.Refills.change || 0;
        }
        product.prescribers.add(item.contact.ID);
      });

      filteredDirectSales.forEach((item: any) => {
        const productId = item.product.ID;
        if (!productMap.has(productId)) {
          productMap.set(productId, {
            productId,
            productName: item.product.name,
            SalesVolume: { value: 0, change: 0 },
            SalesValue: { value: 0, change: 0 },
            accounts: new Set<string>(),
            type: 'direct_sales',
          });
        }

        const product = productMap.get(productId);
        if (item.salesVolume) {
          product.SalesVolume = product.SalesVolume || { value: 0, change: 0 };
          product.SalesVolume.value += item.salesVolume.value || 0;
          product.SalesVolume.change += item.salesVolume.change || 0;
        }
        if (item.salesValue) {
          product.SalesValue = product.SalesValue || { value: 0, change: 0 };
          product.SalesValue.value += item.salesValue.value || 0;
          product.SalesValue.change += item.salesValue.change || 0;
        }
        product.accounts = product.accounts || new Set<string>();
        product.accounts.add(item.account.id);
      });

      const results = Array.from(productMap.values()).map(p => ({
        ...p,
        TRx: roundMetric(p.TRx),
        NRx: roundMetric(p.NRx),
        Refills: roundMetric(p.Refills),
        SalesVolume: roundMetric(p.SalesVolume),
        SalesValue: roundMetric(p.SalesValue),
        prescribers: p.prescribers ? p.prescribers.size : undefined,
        accounts: p.accounts ? p.accounts.size : undefined,
      }));

      return {
        success: true,
        period,
        periodDescription: ({
          CW: 'Current Week',
          C3W: 'Last 3 Weeks',
          C6W: 'Last 6 Weeks',
          CM: 'Current Month',
          C3M: 'Last 3 Months',
          C6M: 'Last 6 Months',
          QTD: 'Quarter to Date',
          YTD: 'Year to Date',
        } as Record<string, string>)[period],
        totalProducts: results.length,
        products: results,
      };
    } catch (error) {
      console.error('[RxTools] Error getting product performance:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get product performance' };
    }
  }

  @AITool(
    'get_top_performing_products',
    'Get top performing products ranked by prescription volume, sales value, or growth',
    [
      {
        name: 'metric',
        type: 'string',
        description:
          'Metric to rank by: TRx (total prescriptions), NRx (new prescriptions), SalesValue, SalesVolume, or Growth (change from last period)',
        required: false,
        enum: ['TRx', 'NRx', 'SalesValue', 'SalesVolume', 'Growth'],
        default: 'TRx',
      },
      {
        name: 'period',
        type: 'string',
        description: 'Time period for analysis',
        required: false,
        enum: ['CW', 'C3W', 'C6W', 'CM', 'C3M', 'C6M', 'QTD', 'YTD'],
        default: 'CM',
      },
      { name: 'limit', type: 'number', description: 'Number of top products to return', required: false, default: 10 },
    ],
  )
  async getTopPerformingProducts(
    metric: 'TRx' | 'NRx' | 'SalesValue' | 'SalesVolume' | 'Growth' = 'TRx',
    period: 'CW' | 'C3W' | 'C6W' | 'CM' | 'C3M' | 'C6M' | 'QTD' | 'YTD' = 'CM',
    limit = 10,
  ): Promise<any> {
    try {
      const performanceResult = await this.getProductPerformance(undefined, period);

      if (!performanceResult.success || !performanceResult.products) {
        return performanceResult;
      }

      // Rank only products that actually carry the requested metric. A direct-sales
      // product has no TRx; leaving it in a TRx ranking yields a ragged array (one item
      // missing the ranked key), which the downstream card model renders unreliably.
      const hasMetric = (p: any) =>
        metric === 'Growth' ? !!(p.TRx || p.SalesValue) : typeof p[metric]?.value === 'number';
      const ranked = performanceResult.products.filter(hasMetric);

      ranked.sort((a: any, b: any) => {
        // Prescription products track growth on TRx.change; direct-sales on SalesValue.change.
        const aValue = metric === 'Growth' ? (a.TRx?.change ?? a.SalesValue?.change ?? 0) : a[metric]?.value ?? 0;
        const bValue = metric === 'Growth' ? (b.TRx?.change ?? b.SalesValue?.change ?? 0) : b[metric]?.value ?? 0;
        return bValue - aValue;
      });

      const topProducts = ranked.slice(0, limit).map((p: any, index: number) => ({
        rank: index + 1,
        productName: p.productName,
        [metric]:
          metric === 'Growth'
            ? round1(p.TRx?.change ?? p.SalesValue?.change ?? 0)
            : roundMetric(p[metric]),
        type: p.type,
        prescribers: p.prescribers,
        accounts: p.accounts,
      }));

      return {
        success: true,
        metric,
        period,
        totalAnalyzed: ranked.length,
        topProducts,
      };
    } catch (error) {
      console.error('[RxTools] Error getting top products:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get top products' };
    }
  }

  @AITool(
    'get_contact_product_adoption',
    "Get a contact's customer SEGMENT (tier) and product adoption-ladder position, per product. Use this to answer \"what segment / which tier is this contact in\" as well as adoption-journey questions — the segment lives here, not on the contact record.",
    [
      { name: 'contact_id', type: 'string', description: 'Contact ID to get adoption information for', required: true },
      { name: 'product_name', type: 'string', description: 'Optional product name to filter adoption data', required: false },
    ],
  )
  async getContactProductAdoption(contact_id: string, product_name?: string): Promise<any> {
    try {
      const contact = this.contactService.getContactByID(contact_id);
      if (!contact) {
        return { success: false, message: `Contact not found with ID: ${contact_id}` };
      }

      if (!await this.waitForAdoptionLadderReady()) {
        return {
          success: true,
          message: 'Adoption ladder data is still syncing — please try again shortly.',
          contact: { id: contact.ID, name: contact.fullName },
          adoptionData: [],
        };
      }

      const customerSegmentations = this.getSegmentationAndLadderRows();

      let contactSegmentations = customerSegmentations.filter((seg: any) => seg.contactId === contact_id);

      if (!contactSegmentations || contactSegmentations.length === 0) {
        return {
          success: true,
          message: 'No segment or adoption-ladder data on record for this contact',
          contact: { id: contact.ID, name: contact.fullName },
          adoptionData: [],
        };
      }

      if (product_name) {
        const searchLower = product_name.toLowerCase();
        contactSegmentations = contactSegmentations.filter((seg: any) => {
          const product = this.brandService.getBrandByID(seg.productId);
          return product?.name?.toLowerCase().includes(searchLower);
        });
      }

      const ladderLevels = await this.adoptionService.getAdoptionLadderData();
      const levelsMap = new Map<string, { name: string; rank: any }>();
      if (ladderLevels && ladderLevels.length > 0) {
        ladderLevels.forEach((level: any) => {
          levelsMap.set(level.indskr_adoptionladderid, {
            name: level.indskr_name,
            rank: level.indskr_rank,
          });
        });
      }

      const formattedAdoption = contactSegmentations.map((seg: any) => {
        const product = this.brandService.getBrandByID(seg.productId);

        let adoptionLevel = 'Not Set';
        let adoptionRank: any = undefined;

        if (seg.adoptionLadderId) {
          const levelData = levelsMap.get(seg.adoptionLadderId);
          if (levelData) {
            adoptionLevel = levelData.name;
            adoptionRank = levelData.rank;
          }
        }

        return {
          productId: seg.productId,
          productName: product?.name || 'Unknown Product',
          adoptionLevel,
          adoptionRank,
          segmentation: seg.indskr_name,
          segmentationId: seg.indskr_customersegmentationid,
        };
      });

      return {
        success: true,
        contact: {
          id: contact.ID,
          name: contact.fullName,
          specialty: (contact as any).specialtyName,
        },
        totalProducts: formattedAdoption.length,
        adoptionData: formattedAdoption,
      };
    } catch (error) {
      console.error('[RxTools] Error getting contact adoption:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get adoption data' };
    }
  }

  @AITool(
    'get_contacts_in_segment',
    'List the rep\'s own contacts that belong to a customer SEGMENT (tier) — e.g. "who are my Tier B contacts". Matches the segment name loosely (partial, case-insensitive).',
    [
      {
        name: 'segment_name',
        type: 'string',
        description: 'Segment / tier name to match, e.g. "Tier B" or "Silver" (partial match allowed)',
        required: true,
      },
    ],
  )
  async getContactsInSegment(segment_name: string): Promise<any> {
    try {
      const term = (segment_name ?? '').trim().toLowerCase();
      if (!term) {
        return { success: false, message: 'A segment name is required.' };
      }

      const customerSegmentations = this.marketScanService.customerSegmentations || [];
      const matched = customerSegmentations.filter((seg: any) => (seg.indskr_name ?? '').toLowerCase().includes(term));

      // A contact can carry the same segment across several products — collapse to one entry per contact.
      // Keep contacts whose record isn't in the rep's local sync too (name unresolved), so the count is
      // honest and matches list_my_segments rather than silently dropping them.
      const byContact = new Map<string, { contactId: string; name: string; nameResolved: boolean; segments: Set<string>; products: Set<string> }>();
      matched.forEach((seg: any) => {
        const contact = this.contactService.getContactByID(seg.contactId);
        let entry = byContact.get(seg.contactId);
        if (!entry) {
          entry = { contactId: seg.contactId, name: contact?.fullName || '', nameResolved: !!contact?.fullName, segments: new Set(), products: new Set() };
          byContact.set(seg.contactId, entry);
        }
        if (seg.indskr_name) entry.segments.add(seg.indskr_name);
        const product = this.brandService.getBrandByID(seg.productId);
        if (product?.name) entry.products.add(product.name);
      });

      const contacts = Array.from(byContact.values())
        .map(e => ({ contactId: e.contactId, name: e.nameResolved ? e.name : null, nameResolved: e.nameResolved, segments: Array.from(e.segments), products: Array.from(e.products) }))
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

      const unresolvedNames = contacts.filter(c => !c.nameResolved).length;
      return { success: true, query: segment_name, totalContacts: contacts.length, unresolvedNames, contacts };
    } catch (error) {
      console.error('[RxTools] Error getting contacts in segment:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get contacts in segment' };
    }
  }

  @AITool(
    'list_my_segments',
    'List the customer SEGMENTS (tiers) present across the rep\'s own contacts, with how many contacts sit in each — use for "what segments do I have" or a segment breakdown.',
    [],
  )
  async listMySegments(): Promise<any> {
    try {
      const customerSegmentations = this.marketScanService.customerSegmentations || [];

      // Count DISTINCT contacts per segment name (a contact can appear once per product).
      const bySegment = new Map<string, Set<string>>();
      customerSegmentations.forEach((seg: any) => {
        const name = seg.indskr_name;
        if (!name || !seg.contactId) return;
        let contacts = bySegment.get(name);
        if (!contacts) {
          contacts = new Set();
          bySegment.set(name, contacts);
        }
        contacts.add(seg.contactId);
      });

      const segments = Array.from(bySegment.entries())
        .map(([segment, contacts]) => ({ segment, contactCount: contacts.size }))
        .sort((a, b) => b.contactCount - a.contactCount);

      return { success: true, totalSegments: segments.length, segments };
    } catch (error) {
      console.error('[RxTools] Error listing segments:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to list segments' };
    }
  }

  @AITool(
    'get_product_adoption_summary',
    'Get adoption ladder distribution summary across all contacts for a product, showing how many contacts are at each adoption stage',
    [
      {
        name: 'product_name',
        type: 'string',
        description:
          'Product name to analyze adoption distribution for (optional - if not provided, shows summary for all products)',
        required: false,
      },
    ],
  )
  async getProductAdoptionSummary(product_name?: string): Promise<any> {
    try {
      if (!await this.waitForAdoptionLadderReady()) {
        return {
          success: true,
          message: 'Adoption ladder data is still syncing — please try again shortly.',
          summary: [],
        };
      }

      const customerSegmentations = this.getSegmentationAndLadderRows();

      if (customerSegmentations.length === 0) {
        return { success: true, message: 'No adoption data available', summary: [] };
      }

      const ladderLevels = await this.adoptionService.getAdoptionLadderData();
      const levelsMap = new Map<string, { name: string; rank: any }>();
      if (ladderLevels && ladderLevels.length > 0) {
        ladderLevels.forEach((level: any) => {
          levelsMap.set(level.indskr_adoptionladderid, {
            name: level.indskr_name,
            rank: level.indskr_rank,
          });
        });
      }

      const productAdoptionMap = new Map<string, any>();

      customerSegmentations.forEach((seg: any) => {
        if (product_name) {
          const product = this.brandService.getBrandByID(seg.productId);
          const searchLower = product_name.toLowerCase();
          if (!product?.name?.toLowerCase().includes(searchLower)) {
            return;
          }
        }

        const productId = seg.productId;
        if (!productAdoptionMap.has(productId)) {
          const product = this.brandService.getBrandByID(productId);
          productAdoptionMap.set(productId, {
            productId,
            productName: product?.name || 'Unknown Product',
            totalContacts: 0,
            adoptionLevels: new Map<string, { levelName: string; rank: any; count: number; percentage: number }>(),
          });
        }

        const productData = productAdoptionMap.get(productId);
        productData.totalContacts++;

        let adoptionLevelName = 'Not Set';
        let adoptionRank: any = undefined;

        if (seg.adoptionLadderId && levelsMap.has(seg.adoptionLadderId)) {
          const levelData = levelsMap.get(seg.adoptionLadderId)!;
          adoptionLevelName = levelData.name;
          adoptionRank = levelData.rank;
        }

        if (!productData.adoptionLevels.has(adoptionLevelName)) {
          productData.adoptionLevels.set(adoptionLevelName, {
            levelName: adoptionLevelName,
            rank: adoptionRank,
            count: 0,
            percentage: 0,
          });
        }
        productData.adoptionLevels.get(adoptionLevelName).count++;
      });

      const summary = Array.from(productAdoptionMap.values()).map(product => {
        const levels = Array.from(product.adoptionLevels.values())
          .map((level: any) => {
            const levelData = level as { levelName: string; rank: number; count: number; percentage: number };
            return {
              levelName: levelData.levelName,
              rank: levelData.rank,
              count: levelData.count,
              percentage: Math.round((levelData.count / product.totalContacts) * 100),
            };
          })
          .sort((a: any, b: any) => (b.rank || 0) - (a.rank || 0));

        return {
          productId: product.productId,
          productName: product.productName,
          totalContacts: product.totalContacts,
          adoptionDistribution: levels,
        };
      });

      return {
        success: true,
        totalProducts: summary.length,
        summary,
      };
    } catch (error) {
      console.error('[RxTools] Error getting adoption summary:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get adoption summary' };
    }
  }

  @AITool(
    'analyze_product_prescribing_patterns',
    'Analyze prescription patterns for a product across all prescribers to identify trends, top prescribers, and growth opportunities',
    [
      { name: 'product_name', type: 'string', description: 'Product name to analyze prescribing patterns for', required: true },
      {
        name: 'period',
        type: 'string',
        description: 'Time period for analysis',
        required: false,
        enum: ['CW', 'C3W', 'C6W', 'CM', 'C3M', 'C6M', 'QTD', 'YTD'],
        default: 'CM',
      },
    ],
  )
  async analyzeProductPrescribingPatterns(
    product_name: string,
    period: 'CW' | 'C3W' | 'C6W' | 'CM' | 'C3M' | 'C6M' | 'QTD' | 'YTD' = 'CM',
  ): Promise<any> {
    try {
      const rxData = await firstValueFrom(this.rxSalesDataService.rxData$);

      const periodEnum = ({
        CW: 1,
        C3W: 2,
        C6W: 3,
        CM: 4,
        C3M: 5,
        C6M: 6,
        QTD: 7,
        YTD: 8,
      } as Record<string, number>)[period] || 4;

      const searchLower = product_name.toLowerCase();
      const productData = rxData.filter((d: any) =>
        d.product.name?.toLowerCase().includes(searchLower) && d.period === periodEnum,
      );

      if (productData.length === 0) {
        return {
          success: true,
          message: `No prescribing data found for product: ${product_name}`,
          productName: product_name,
          period,
        };
      }

      const prescriberStats = productData
        .map((d: any) => ({
          contactId: d.contact.ID,
          contactName: d.contact.fullName,
          specialty: d.contact.specialtyName,
          TRx: d.TRx?.value || 0,
          NRx: d.NRx?.value || 0,
          Refills: d.Refills?.value || 0,
          growth: d.TRx?.change || 0,
          newPrescriptionRate: d.TRx?.value
            ? ((d.NRx?.value || 0) / d.TRx.value * 100).toFixed(1) + '%'
            : '0%',
        }))
        .sort((a: any, b: any) => b.TRx - a.TRx);

      const totalTRx = prescriberStats.reduce((sum: number, p: any) => sum + p.TRx, 0);
      const totalNRx = prescriberStats.reduce((sum: number, p: any) => sum + p.NRx, 0);
      const totalGrowth = prescriberStats.reduce((sum: number, p: any) => sum + p.growth, 0);
      const avgTRxPerPrescriber = totalTRx / prescriberStats.length;

      // Full roster, sorted by TRx desc — the agent decides how many to surface.
      const topPrescribers = prescriberStats;
      const growingPrescribers = prescriberStats
        .filter((p: any) => p.growth > 0)
        .sort((a: any, b: any) => b.growth - a.growth);
      const decliningPrescribers = prescriberStats
        .filter((p: any) => p.growth < 0)
        .sort((a: any, b: any) => a.growth - b.growth);

      return {
        success: true,
        productName: (productData[0] as any).product.name,
        period,
        summary: {
          totalPrescribers: prescriberStats.length,
          totalTRx,
          totalNRx,
          totalGrowth,
          avgTRxPerPrescriber: Math.round(avgTRxPerPrescriber),
          prescribersGrowing: growingPrescribers.length,
          prescribersDeclining: decliningPrescribers.length,
        },
        topPrescribers,
        growingPrescribers,
        decliningPrescribers,
      };
    } catch (error) {
      console.error('[RxTools] Error analyzing prescribing patterns:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to analyze patterns' };
    }
  }
}
